import React, { useMemo, useState } from 'react';

import { useInView } from 'react-intersection-observer';

import { ApiObject, Program } from 'app/types';

import styles from './Tabs.module.scss';
import { useMediaQuery } from 'react-responsive';
import { navigateTop } from 'common/utils';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { SessionState } from 'features/session/sessionSlice';
import {
  setFavorites,
  UserState,
  setSchoolShortlisted,
  setLastStepDone,
} from 'features/user/userSlice';

import { useAddFavoriteMutation, useDeleteFavoriteMutation } from 'services/exploreApi';
import { useNavigate } from 'react-router-dom';

interface CompProps {
  program: ApiObject<Program> | undefined;
  programTopic: number;
}

interface CheckProps {
  text: string;
}

const CheckComponent = ({ text }: CheckProps): React.ReactElement => {
  return (
    <div className={styles.checkContainer}>
      <i className="bi bi-check-circle-fill"> </i>
      <p> {text} </p>
    </div>
  );
};

export const TabsComponent = ({ program, programTopic }: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const { favorites } = useAppSelector(({ user }: { user: UserState }) => user);

  const [isFavorite, setFavorite] = useState<boolean>(
    program?.data.favorite ? program.data.favorite : false
  );
  const { ref: programTabsRef, inView: programTabsInView } = useInView({
    threshold: 1,
  });
  const { ref: mobileTabsRef, inView: mobileRefInView } = useInView({
    threshold: 1,
  });

  const handleAddFavorite = () => {
    const values = { id: program?.data.id, kind: 'Program' };
    if (logged) {
      addFavorite(values).then((resp: any) => {
        setFavorite(!isFavorite);
        if (program && !favorites?.includes(program.data.id)) {
          dispatch(setFavorites([...(favorites || []), program.data.id]));
        }
        dispatch(setSchoolShortlisted(true));
        dispatch(setLastStepDone(5));
      });
    } else {
      setTimeout(() => {
        navigate('/signup');
      }, 100);
    }
  };
  const handleDeleteFavorite = () => {
    const values = { id: program?.data.id, kind: 'Program' };
    deleteFavorite(values).then((resp: any) => {
      setFavorite(!isFavorite);
      dispatch(setFavorites(favorites?.filter((id: number) => id !== program?.data.id)));
    });
  };

  const showCareerSection = useMemo(
    () =>
      program?.data.school.school_rankings?.[0] ||
      (program?.data.school.working_after_months &&
        program?.data.school.working_after_percentage) ||
      program?.data.school.alumni_information ||
      (program?.data.school.companies && program?.data.school.companies.length > 0) ||
      program?.data.school.career_service_description,
    [program]
  );
  const showVisaSection = useMemo(
    () =>
      (program?.data.school.city.country.working_visa_number &&
        program?.data.school.city.country.working_visa_format) ||
      program?.data.school.city.country.working_visa_after_study ||
      program?.data.school.city.country.visa_description,
    [program]
  );

  return (
    <div className={styles.sectionsTabsDT}>
      {isMobile ? (
        <div className={styles.tabsContainer}>
          <div className={styles.mobileTabsRef} ref={mobileTabsRef} />
          <div className={`${styles.blockTabs} ${!mobileRefInView ? styles.fixedTabs : ''}`}>
            <a
              className={`${styles.tabs} ${programTopic === 0 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'overview', true, true)}
            >
              Overview
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 1 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'program-profile', true, true)}
            >
              Program profile
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 2 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'apply', true, true)}
            >
              Apply
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 3 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'fee-costs', true, true)}
            >
              Fee & costs
            </a>
            {showCareerSection && (
              <a
                className={`${styles.tabs} ${programTopic === 4 ? styles.active : ''}`}
                onClick={() => navigateTop(0, 0, 'career', true, true)}
              >
                Career
              </a>
            )}
            {showVisaSection && (
              <a
                className={`${styles.tabs} ${programTopic === 5 ? styles.active : ''}`}
                onClick={() => navigateTop(0, 0, 'visa', true, true)}
              >
                Visa
              </a>
            )}
            <a
              className={`${styles.tabs} ${programTopic === 6 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'student-life', true, true)}
            >
              Student life
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 7 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'faq', true, true)}
            >
              FAQs & links
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 8 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'similar-programs', true, true)}
            >
              Similar programs
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 9 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'school-overview', true, true)}
            >
              Overview of program university
            </a>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.referenceDiv} ref={programTabsRef}></div>
          <div
            className={`${styles.blockTabs} ${
              programTabsInView ? styles.notFixed : styles.fixedTabs
            }`}
          >
            <a
              className={`${styles.tabs} ${programTopic === 0 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'overview', true, true)}
            >
              Overview
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 1 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'program-profile', true, true)}
            >
              Program profile
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 2 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'apply', true, true)}
            >
              Apply
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 3 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'fee-costs', true, true)}
            >
              Fee & costs
            </a>
            {showCareerSection && (
              <a
                className={`${styles.tabs} ${programTopic === 4 ? styles.active : ''}`}
                onClick={() => navigateTop(0, 0, 'career', true, true)}
              >
                Career
              </a>
            )}
            {showVisaSection && (
              <a
                className={`${styles.tabs} ${programTopic === 5 ? styles.active : ''}`}
                onClick={() => navigateTop(0, 0, 'visa', true, true)}
              >
                Visa
              </a>
            )}
            <a
              className={`${styles.tabs} ${programTopic === 6 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'student-life', true, true)}
            >
              Student life
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 7 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'faq', true, true)}
            >
              FAQs & links
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 8 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'similar-programs', true, true)}
            >
              Similar programs
            </a>
            <a
              className={`${styles.tabs} ${programTopic === 9 ? styles.active : ''}`}
              onClick={() => navigateTop(0, 0, 'school-overview', true, true)}
            >
              Overview of program university
            </a>
            <button
              className={`btn btn-primary btn-lg ${styles.favoriteButton}`}
              onClick={() => (isFavorite ? handleDeleteFavorite() : handleAddFavorite())}
            >
              {isFavorite ? 'Shortlisted' : 'Shortlist'}
              {isFavorite ? (
                <div className={styles.big_heart_fill}>
                  <i className="bi bi-heart-fill"></i>
                </div>
              ) : (
                <div className={styles.big_heart}>
                  <i className="bi bi-heart"></i>
                </div>
              )}
            </button>
            <a
              className="btn btn-primary btn-lg"
              href={`https://unifyapply.com/experts/request-meeting?program_id=${program?.data.id}&program_name=${program?.data.name}`}
            >
              Get free expert help to apply
            </a>
            <CheckComponent text="Ask details about this program" />
            <CheckComponent text="Set an application plan for you" />
            <CheckComponent text="Support for application journey" />
          </div>
        </>
      )}
    </div>
  );
};
